import { Box, ButtonBase, Container, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useBreakpoint } from "../../../hooks/useBreakpoint";
import GoogleTranslate from "../../common/GoogleTranslate";
import CartDrawer from "../cart/CartDrawer";
import CartIcon from "../cart/CartIcon";

import { navTokens } from "./navTokens";
import SearchBar from "./SearchBar";
import UserActions from "./UserActions";

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet, isLaptop } = useBreakpoint();
  const [cartDrawerOpen, setCartDrawerOpen] = React.useState(false);
  const { t: myTranslate } = useTranslation();
  const enableGoogleTranslate = React.useMemo(() => {
    return location.pathname.startsWith("/product");
  }, [location.pathname]);

  const searchVariant = isMobile
    ? "mobile"
    : isTablet || isLaptop
      ? "compact"
      : "desktop";
  const actionHeight = isMobile ? "auto" : isTablet || isLaptop ? 46 : 52;

  return (
    <Box
      data-app-header
      data-gt-ignore="true"
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        backdropFilter: { xs: "none", md: "saturate(180%) blur(10px)" },
        WebkitBackdropFilter: { xs: "none", md: "saturate(180%) blur(10px)" },
        borderBottom: `1px solid ${navTokens.border}`,
        borderRadius: 0,
        pt: {
          xs: "calc(env(safe-area-inset-top, 0px) + 8px)",
          md: "calc(env(safe-area-inset-top, 0px) + 6px)",
        },
        pb: { xs: 1, md: 0.75 },
      }}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1.5, md: 2 } }}>
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateAreas: isMobile
              ? `"logo actions" "search search"`
              : `"logo search actions"`,
            gridTemplateColumns: isMobile
              ? "1fr auto"
              : "auto minmax(280px, 1fr) auto",
            alignItems: "center",
            columnGap: { xs: 1, md: 2 },
            rowGap: isMobile ? 1.25 : 0,
            py: isMobile ? 0.5 : 1,
          }}
        >
          <ButtonBase
            onClick={() => navigate("/")}
            aria-label="Go to home page"
            disableRipple
            sx={{
              gridArea: "logo",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexShrink: 0,
              width: "fit-content",
              borderRadius: 2,
              px: 0.5,
              transition: "background-color 0.2s ease",
              "&:hover": { backgroundColor: navTokens.bgMuted },
            }}
          >
            <Box
              component="img"
              src="/logos/logo-png.png"
              alt="US2TJK Logo"
              sx={{
                height: isMobile ? 36 : 54,
                width: "auto",
                objectFit: "contain",
                display: "block",
              }}
            />
            <Box
              component="img"
              src="/logos/title.png"
              alt="US2TJK Title"
              sx={{
                mt: isMobile ? 0 : "6px",
                height: isMobile ? 18 : 20,
                width: "auto",
                maxWidth: isMobile ? 120 : 140,
                objectFit: "contain",
                display: "block",
              }}
            />
          </ButtonBase>

          <Box
            sx={{
              gridArea: "search",
              minWidth: 0,
              width: "100%",
              alignSelf: "center",
            }}
          >
            <SearchBar variant={searchVariant} fullWidth />
          </Box>

          <Box
            sx={{
              gridArea: "actions",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: { xs: 1, md: 1.5 },
              minWidth: 0,
              minHeight: actionHeight,
              alignSelf: "center",
            }}
          >
            {isMobile ? (
              <GoogleTranslate enableGoogleTranslate={enableGoogleTranslate} />
            ) : (
              <>
                <GoogleTranslate enableGoogleTranslate={enableGoogleTranslate} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 56,
                  }}
                >
                  <CartIcon
                    size="medium"
                    showTooltip={true}
                    onClick={() => setCartDrawerOpen(true)}
                  />
                  <Typography
                    variant="caption"
                    className="notranslate"
                    translate="no"
                    sx={{
                      color: navTokens.textMuted,
                      fontSize: "11px",
                      lineHeight: 1,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    {myTranslate("navbar.cart")}
                  </Typography>
                </Box>
                <UserActions />
              </>
            )}
          </Box>
        </Box>
      </Container>

      <CartDrawer
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
      />
    </Box>
  );
};

export default Navigation;
